import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { StatusPill } from "@/components/parcelio/status-pill";
import { LiveMap, markStops } from "@/components/parcelio/live-map";
import { CommsPanel, type CommsMode } from "@/components/parcelio/comms-panel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  parcelsApi,
  directoryApi,
  conversationsApi,
  ApiError,
  type Parcel,
  type ParcelEvent,
  type ParcelLocation,
  type DriverDirectoryEntry,
  type GpsPing,
} from "@/lib/api";
import {
  formatDateTime,
  formatParcelValue,
  initials,
  parcelProgress,
  parcelStatusLabel,
  toLegacyStatus,
} from "@/lib/parcel-adapters";
import { Phone, MessageSquare, Video, ArrowLeft, Star, Package, DollarSign, Navigation, AlertTriangle, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

export default function ParcelDetail() {
  const { parcelId } = useParams();
  const token = useAuth((s) => s.session?.accessToken);
  const [parcel, setParcel] = useState<Parcel | null>(null);
  const [driver, setDriver] = useState<DriverDirectoryEntry | null>(null);
  const [allDrivers, setAllDrivers] = useState<DriverDirectoryEntry[]>([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignDriverId, setReassignDriverId] = useState("");
  const [reassigning, setReassigning] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [events, setEvents] = useState<ParcelEvent[]>([]);
  const [location, setLocation] = useState<ParcelLocation>(null);
  const [history, setHistory] = useState<GpsPing[]>([]);
  const [loading, setLoading] = useState(true);
  const [comms, setComms] = useState<{ mode: CommsMode; peer: { name: string; initials: string; role: string }; conversationId: string | null } | null>(null);
  const [insuranceOpen, setInsuranceOpen] = useState(false);
  const [insuranceForm, setInsuranceForm] = useState({ gender: "Male", date_of_birth: "", address: "", vehicle_plate_number: "" });
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    if (!parcelId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [p, ev, loc, hist] = await Promise.all([
          parcelsApi.get(parcelId, token),
          parcelsApi.events(parcelId, token).catch(() => []),
          parcelsApi.location(parcelId, token).catch(() => null),
          parcelsApi.gpsHistory(parcelId, undefined, token).catch(() => []),
        ]);
        if (cancelled) return;
        setParcel(p);
        setEvents(ev);
        setLocation(loc);
        setHistory(hist);
        const drivers = await directoryApi.drivers(token).catch(() => []);
        if (cancelled) return;
        setAllDrivers(drivers);
        setDriver(p.driver_id ? drivers.find((d) => d.id === p.driver_id) ?? null : null);
      } catch {
        toast.error("Failed to load parcel");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [parcelId]);

  if (loading) {
    return <LoadingBlock label="Loading parcel…" />;
  }
  if (!parcel) {
    return <div className="p-6 text-sm text-muted-foreground">Parcel not found.</div>;
  }

  const openDriverComms = async (mode: CommsMode) => {
    if (!driver) return;
    const peer = { name: driver.name, initials: initials(driver.name), role: `Driver${driver.vehicle_plate ? ` · ${driver.vehicle_plate}` : ""}` };
    if (mode !== "chat") {
      setComms({ mode, peer, conversationId: null });
      return;
    }
    setComms({ mode, peer, conversationId: null });
    try {
      const conv = await conversationsApi.forParcel(parcel.id, "driver", token);
      setComms({ mode, peer, conversationId: conv.id });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not open chat with driver");
      setComms(null);
    }
  };
  const openRecipientComms = (mode: CommsMode) => {
    if (!parcel.receiver_name) return;
    // The recipient has no login account (only name/phone on file) — chat
    // mode opens with no conversationId, which the panel shows as
    // unavailable rather than faking a two-way thread that can't exist.
    setComms({
      mode,
      peer: { name: parcel.receiver_name, initials: initials(parcel.receiver_name), role: "Recipient" },
      conversationId: null,
    });
  };

  const buyInsurance = async () => {
    if (!parcelId) return;
    if (!insuranceForm.date_of_birth || !insuranceForm.address || !insuranceForm.vehicle_plate_number) {
      return toast.error("Date of birth, address, and vehicle plate are required");
    }
    setPurchasing(true);
    try {
      const updated = await parcelsApi.purchaseInsurance(parcelId, insuranceForm, token);
      setParcel(updated);
      toast.success("Insurance purchased");
      setInsuranceOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not purchase insurance");
    } finally {
      setPurchasing(false);
    }
  };

  const openReassign = () => {
    setReassignDriverId(parcel.driver_id ?? "");
    setReassignOpen(true);
  };

  const saveReassign = async () => {
    if (!reassignDriverId) return toast.error("Select a driver");
    setReassigning(true);
    try {
      const updated = await parcelsApi.update(parcel.id, { driver_id: reassignDriverId }, token);
      setParcel(updated);
      setDriver(allDrivers.find((d) => d.id === reassignDriverId) ?? null);
      toast.success("Driver reassigned");
      setReassignOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not reassign driver");
    } finally {
      setReassigning(false);
    }
  };

  // Built with DOM APIs (not document.write/innerHTML) since parcel fields
  // (addresses, receiver name) are user-supplied text rendered into a raw
  // popup document — textContent avoids any script-injection risk.
  const printLabel = () => {
    const win = window.open("", "_blank", "width=420,height=600");
    if (!win) return toast.error("Allow popups to print the label");
    const doc = win.document;
    doc.title = `Label — ${parcel.tracking_id}`;

    const style = doc.createElement("style");
    style.textContent = `
      body { font-family: system-ui, sans-serif; padding: 24px; text-align: center; }
      h1 { font-size: 16px; letter-spacing: 1px; margin: 16px 0 4px; }
      .addr { text-align: left; font-size: 13px; margin: 16px auto; max-width: 320px; }
      .addr div { margin-bottom: 10px; }
      .label { font-size: 10px; text-transform: uppercase; color: #666; }
      img { width: 200px; height: 200px; }
    `;
    doc.head.appendChild(style);

    const img = doc.createElement("img");
    img.src = parcelsApi.qrUrl(parcel.tracking_id);
    img.alt = "QR code";
    doc.body.appendChild(img);

    const h1 = doc.createElement("h1");
    h1.textContent = parcel.tracking_id;
    doc.body.appendChild(h1);

    const addrBlock = doc.createElement("div");
    addrBlock.className = "addr";

    const fromLine = doc.createElement("div");
    const fromLabel = doc.createElement("div");
    fromLabel.className = "label";
    fromLabel.textContent = "From";
    fromLine.appendChild(fromLabel);
    fromLine.appendChild(doc.createTextNode(parcel.pickup_addr ?? "—"));
    addrBlock.appendChild(fromLine);

    const toLine = doc.createElement("div");
    const toLabel = doc.createElement("div");
    toLabel.className = "label";
    toLabel.textContent = "To";
    toLine.appendChild(toLabel);
    toLine.appendChild(doc.createTextNode(parcel.receiver_name ?? "—"));
    toLine.appendChild(doc.createElement("br"));
    toLine.appendChild(doc.createTextNode(parcel.dropoff_addr ?? "—"));
    addrBlock.appendChild(toLine);

    doc.body.appendChild(addrBlock);

    const triggerPrint = () => win.print();
    if (!img.complete) {
      img.onload = triggerPrint;
      img.onerror = triggerPrint;
    } else {
      triggerPrint();
    }
  };

  const cancelDelivery = async () => {
    setCancelling(true);
    try {
      const res = await parcelsApi.scan(parcel.id, "cancelled", undefined, token);
      setParcel((cur) => (cur ? { ...cur, status: res.status as Parcel["status"] } : cur));
      toast.success("Delivery cancelled");
      setCancelOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not cancel delivery");
    } finally {
      setCancelling(false);
    }
  };

  const legacyStatus = toLegacyStatus(parcel.status);
  const firstPing = history.length > 0 ? history[0] : null;
  const lastPing = history.length > 0 ? history[history.length - 1] : null;
  const mapOrigin =
    parcel.pickup_lat != null && parcel.pickup_lng != null
      ? { lat: parcel.pickup_lat, lng: parcel.pickup_lng, label: "Start" }
      : firstPing
        ? { lat: firstPing.lat, lng: firstPing.lng, label: "Start" }
        : null;
  const mapDestination =
    parcel.dropoff_lat != null && parcel.dropoff_lng != null
      ? { lat: parcel.dropoff_lat, lng: parcel.dropoff_lng, label: "Destination" }
      : null;
  const mapCurrent = location
    ? { lat: location.lat, lng: location.lng, label: "Live position" }
    : lastPing
      ? { lat: lastPing.lat, lng: lastPing.lng, label: "Live position" }
      : null;
  const stopFlags = markStops(history);

  return (
    <>
      <Topbar
        title={`Tracking · ${parcel.tracking_id}`}
        subtitle={parcel.receiver_name ?? "—"}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to="/app/tracking"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Link>
          </Button>
        }
      />
      <div className="flex-1 p-4 md:p-6">
        <div className="grid gap-4 lg:grid-cols-[1fr,380px]">
          {/* Map + timeline */}
          <div className="space-y-4">
            <div className="surface-card overflow-hidden">
              <LiveMap origin={mapOrigin} destination={mapDestination} current={mapCurrent} history={history} className="aspect-[16/9] w-full" />
              <div className="grid gap-3 border-t p-4 sm:grid-cols-2 lg:grid-cols-4">
                <MetaBlock icon={Navigation} label="Destination" value={parcel.dropoff_addr ?? "—"} />
                <MetaBlock icon={Package} label="Status" value={<StatusPill status={legacyStatus} />} />
                <MetaBlock icon={DollarSign} label="Declared value" value={formatParcelValue(parcel.amount_kobo)} />
                <MetaBlock
                  icon={ShieldCheck}
                  label="Goods-in-transit cover"
                  value={
                    parcel.insurance_status === "unavailable" ? (
                      "Not available yet"
                    ) : parcel.insurance_status === "quoted" ? (
                      <div className="flex items-center gap-2">
                        <span>{parcel.insurance_premium_kobo != null ? formatParcelValue(parcel.insurance_premium_kobo) : "quoted"}</span>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => setInsuranceOpen(true)}>
                          Buy cover
                        </Button>
                      </div>
                    ) : parcel.insurance_status === "active" ? (
                      <span className="text-success">
                        Active{parcel.insurance_policy_number ? ` · ${parcel.insurance_policy_number}` : ""}
                        {parcel.insurance_certificate_url ? (
                          <>
                            {" "}
                            · <a href={parcel.insurance_certificate_url} target="_blank" rel="noreferrer" className="underline">certificate</a>
                          </>
                        ) : null}
                      </span>
                    ) : (
                      "Failed"
                    )
                  }
                />
              </div>
            </div>

            <div className="surface-card p-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold">Delivery progress</h3>
                <span className="text-sm font-medium">{parcelStatusLabel(parcel.status)}</span>
              </div>
              <Progress value={parcelProgress(parcel.status)} className="h-2" />
              <p className="mt-1 text-xs text-muted-foreground">
                Progress is a client-side estimate based on status — the backend does not report a live percentage or ETA.
              </p>
            </div>

            <div className="surface-card p-5">
              <h3 className="mb-4 text-sm font-semibold">Timeline</h3>
              {events.length > 0 ? (
                <ol className="relative space-y-4 border-l pl-6">
                  {events.map((e, i) => (
                    <li key={i} className="relative">
                      <span className="absolute -left-[29px] top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-success text-success-foreground ring-2 ring-surface">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3"><path d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" /></svg>
                      </span>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-sm font-medium">{e.kind.replace(/_/g, " ")}</span>
                        <span className="text-xs text-muted-foreground">{formatDateTime(e.occurred_at)}</span>
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-muted-foreground">No status history yet.</p>
              )}
              {parcel.status === "failed" ? (
                <div className="mt-5 flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <div className="flex-1 text-sm">
                    <div className="font-medium text-destructive">Delivery failed</div>
                    <p className="text-xs text-muted-foreground">Retry scheduled, or reassign to another driver.</p>
                  </div>
                  <Button size="sm" variant="destructive">Retry now</Button>
                </div>
              ) : null}
            </div>

            <div className="surface-card overflow-hidden">
              <div className="flex items-center justify-between border-b p-4">
                <div>
                  <h3 className="text-sm font-semibold">Vehicle GPS pings (last 24h)</h3>
                  <p className="text-xs text-muted-foreground">Location reports from the vehicle carrying this parcel — the parcel moves when the vehicle does.</p>
                </div>
                <span className="text-xs text-muted-foreground">{history.length} ping{history.length === 1 ? "" : "s"}</span>
              </div>
              <div className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Lat</TableHead>
                      <TableHead>Lng</TableHead>
                      <TableHead>Speed</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                          No vehicle pings in the last 24h.
                        </TableCell>
                      </TableRow>
                    ) : (
                      [...history]
                        .map((p, i) => ({ ...p, stopped: stopFlags[i] }))
                        .reverse()
                        .map((p, i) => (
                          <TableRow key={`${p.recorded_at}-${i}`}>
                            <TableCell className="whitespace-nowrap text-xs">{new Date(p.recorded_at).toLocaleTimeString()}</TableCell>
                            <TableCell className="font-mono text-xs">{p.lat.toFixed(5)}</TableCell>
                            <TableCell className="font-mono text-xs">{p.lng.toFixed(5)}</TableCell>
                            <TableCell className="text-xs">{p.speed_kmh != null ? `${p.speed_kmh.toFixed(0)} km/h` : "—"}</TableCell>
                            <TableCell>
                              {p.stopped ? (
                                <Badge variant="outline" className="border-purple-400/30 bg-purple-400/10 text-purple-600 dark:text-purple-400">Stopped</Badge>
                              ) : (
                                <Badge variant="outline" className="border-success/30 bg-success/10 text-success">Moving</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* Driver + recipient */}
          <aside className="space-y-4">
            {driver ? (
              <div className="surface-card p-5">
                <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Assigned driver</div>
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold">
                    {initials(driver.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold">{driver.name}</div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Star className="h-3 w-3 fill-warning text-warning" /> {driver.rating}{driver.vehicle_plate ? ` · ${driver.vehicle_plate}` : ""}
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <Button variant="outline" size="sm" className="flex-col h-auto py-2.5" onClick={() => openDriverComms("chat")}>
                    <MessageSquare className="h-4 w-4" /><span className="mt-1 text-[10px]">Chat</span>
                  </Button>
                  <Button variant="outline" size="sm" className="flex-col h-auto py-2.5" onClick={() => openDriverComms("voice")}>
                    <Phone className="h-4 w-4" /><span className="mt-1 text-[10px]">Call</span>
                  </Button>
                  <Button size="sm" className="flex-col h-auto py-2.5" onClick={() => openDriverComms("video")}>
                    <Video className="h-4 w-4" /><span className="mt-1 text-[10px]">Video</span>
                  </Button>
                </div>
                <div className="mt-4 space-y-2 text-xs">
                  <Row label="Plate" value={driver.vehicle_plate ?? "—"} />
                  <Row label="Phone" value={driver.phone ?? "—"} />
                  <Row label="Last location" value={driver.last_location ?? "—"} />
                </div>
              </div>
            ) : (
              <div className="surface-card p-5 text-sm text-muted-foreground">No driver assigned yet.</div>
            )}

            <div className="surface-card p-5">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recipient</div>
              <div className="font-semibold">{parcel.receiver_name ?? "—"}</div>
              <div className="text-sm text-muted-foreground">{parcel.dropoff_addr ?? "—"}</div>
              {parcel.dropoff_postcode ? (
                <Badge variant="outline" className="mt-1.5 gap-1 text-[10px]">
                  <ShieldCheck className="h-3 w-3" /> NIPOST-verified · {parcel.dropoff_postcode}
                </Badge>
              ) : null}
              <div className="mt-4 grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm" onClick={() => openRecipientComms("voice")}><Phone className="mr-1 h-3.5 w-3.5" /> Call</Button>
                <Button variant="outline" size="sm" onClick={() => openRecipientComms("chat")}><MessageSquare className="mr-1 h-3.5 w-3.5" /> Chat</Button>
                <Button size="sm" onClick={() => openRecipientComms("video")}><Video className="mr-1 h-3.5 w-3.5" /> Video</Button>
              </div>
            </div>

            <div className="surface-card p-5">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actions</div>
              <div className="grid gap-2">
                <Button variant="secondary" size="sm" onClick={openReassign}>Reassign driver</Button>
                <Button variant="secondary" size="sm" onClick={printLabel}>Print label</Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setCancelOpen(true)}
                  disabled={parcel.status === "delivered" || parcel.status === "cancelled"}
                >
                  Cancel delivery
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </div>
      <CommsPanel
        open={!!comms}
        onOpenChange={(v) => !v && setComms(null)}
        peer={comms?.peer ?? { name: "", initials: "" }}
        initialMode={comms?.mode ?? "chat"}
        conversationId={comms?.conversationId ?? null}
        token={token}
      />

      <Dialog open={insuranceOpen} onOpenChange={setInsuranceOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Buy goods-in-transit cover</DialogTitle>
            <DialogDescription>
              A few underwriting details for the insured person are needed to complete the purchase.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Gender</Label>
              <Select value={insuranceForm.gender} onValueChange={(v) => setInsuranceForm({ ...insuranceForm, gender: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date of birth</Label>
              <Input type="date" value={insuranceForm.date_of_birth} onChange={(e) => setInsuranceForm({ ...insuranceForm, date_of_birth: e.target.value })} />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={insuranceForm.address} onChange={(e) => setInsuranceForm({ ...insuranceForm, address: e.target.value })} placeholder="123 Main St." />
            </div>
            <div>
              <Label>Vehicle plate number</Label>
              <Input value={insuranceForm.vehicle_plate_number} onChange={(e) => setInsuranceForm({ ...insuranceForm, vehicle_plate_number: e.target.value })} placeholder="Carrying vehicle's plate" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInsuranceOpen(false)}>Cancel</Button>
            <Button onClick={buyInsurance} disabled={purchasing}>Buy cover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reassign driver</DialogTitle>
            <DialogDescription>Moves this delivery onto whichever vehicle the new driver currently drives.</DialogDescription>
          </DialogHeader>
          <Select value={reassignDriverId || "none"} onValueChange={(v) => setReassignDriverId(v === "none" ? "" : v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select a driver" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassigned</SelectItem>
              {allDrivers.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}{d.vehicle_plate ? ` · ${d.vehicle_plate}` : " · no vehicle"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)} disabled={reassigning}>Cancel</Button>
            <Button onClick={saveReassign} disabled={reassigning}>{reassigning ? "Saving…" : "Reassign"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this delivery?</AlertDialogTitle>
            <AlertDialogDescription>
              Delivery {parcel.tracking_id} will be marked cancelled. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Keep delivery</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={cancelling}
              onClick={(e) => {
                e.preventDefault();
                cancelDelivery();
              }}
            >
              {cancelling ? "Cancelling…" : "Cancel delivery"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function MetaBlock({ icon: Icon, label, value }: { icon: typeof Package; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="truncate text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
